import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Color, NgxChartsModule, ScaleType } from '@swimlane/ngx-charts';
import { SalesService } from '../../../../shared/services/apis/platform/sales.service';
import { AdminSalesTimeSeriesPoint } from '../../../../shared/services/responses/platform/admin-sales.response';

type Metric = 'books' | 'revenue' | 'orders';
type Granularity = 'day' | 'week' | 'month';

/** Evolução de livros vendidos, faturamento e pedidos no tempo (vendas aprovadas). */
@Component({
  selector: 'app-sales-chart-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, NgxChartsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="head">
      <div>
        <h2 mat-dialog-title class="title">Evolução das vendas</h2>
        <p class="intro">Livros vendidos, faturamento e pedidos por período. Considera apenas vendas aprovadas.</p>
      </div>
      <button mat-icon-button (click)="close()" aria-label="Fechar"><mat-icon>close</mat-icon></button>
    </div>
    <mat-dialog-content class="content">
      <div class="controls">
        <div class="seg" role="group" aria-label="Métrica">
          <button *ngFor="let m of metrics" [class.on]="metric === m.key" (click)="setMetric(m.key)">{{ m.label }}</button>
        </div>
        <div class="seg" role="group" aria-label="Período">
          <button *ngFor="let r of ranges" [class.on]="rangeDays === r.days" (click)="setRange(r.days)">{{ r.label }}</button>
        </div>
        <div class="seg" role="group" aria-label="Granularidade">
          <button *ngFor="let g of granularities" [class.on]="granularity === g.key" (click)="setGranularity(g.key)">{{ g.label }}</button>
        </div>
        <span class="sum" *ngIf="!loading">Período: <b>{{ totalBooks }}</b> livros · <b>{{ totalOrders }}</b> pedidos · <b>{{ totalRevenue | currency:'BRL':'symbol':'1.2-2' }}</b></span>
      </div>

      <div class="chart-wrap">
        <div class="spinner" *ngIf="loading"><mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner></div>
        <ngx-charts-line-chart *ngIf="!loading && series.length"
          [results]="series" [scheme]="scheme" [xAxis]="true" [yAxis]="true" [autoScale]="false" [yScaleMin]="0"
          [roundDomains]="true" [legend]="false" [animations]="false" [timeline]="false" [showGridLines]="true"
          [yAxisTickFormatting]="yFmt">
        </ngx-charts-line-chart>
        <div class="empty" *ngIf="!loading && !series.length">Sem vendas no período.</div>
      </div>

      <div class="table-wrap" *ngIf="!loading && points.length">
        <table>
          <thead><tr><th>Período</th><th class="num">Livros</th><th class="num">Pedidos</th><th class="num">Faturamento</th></tr></thead>
          <tbody>
            <tr *ngFor="let p of pointsDesc"><td>{{ label(p.date) }}</td><td class="num">{{ p.books }}</td><td class="num">{{ p.orders }}</td><td class="num">{{ p.revenue | currency:'BRL':'symbol':'1.2-2' }}</td></tr>
          </tbody>
        </table>
      </div>
      <div class="error" *ngIf="error">{{ error }}</div>
    </mat-dialog-content>
  `,
  styles: [`
    .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; padding: 16px 24px 0; }
    .title { font-weight: 800; font-size: 1.15rem; margin: 0; padding: 0; }
    .intro { color: #4b5563; margin: 4px 0 0; font-size: .9rem; }
    .content { width: min(960px, 92vw); }
    .controls { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin: 10px 0 12px; }
    .seg { display: inline-flex; border: 1px solid #cfd6d1; border-radius: 8px; overflow: hidden; }
    .seg button { border: 0; background: none; padding: 6px 12px; font: inherit; font-size: .85rem; cursor: pointer; color: #1f2a24; }
    .seg button.on { background: #1f2a24; color: #fff; font-weight: 600; }
    .sum { margin-left: auto; font-size: .85rem; color: #55635b; }
    .chart-wrap { height: 320px; position: relative; }
    .spinner, .empty { position: absolute; inset: 0; display: grid; place-items: center; color: #8a958e; }
    .table-wrap { max-height: 200px; overflow: auto; border: 1px solid #e3e7e3; border-radius: 8px; margin-top: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: .85rem; }
    th, td { padding: 6px 10px; border-bottom: 1px solid #eef1ed; text-align: left; }
    th { position: sticky; top: 0; background: #fafbf9; font-size: .72rem; text-transform: uppercase; letter-spacing: .06em; color: #8a958e; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .error { color: #c62828; margin-top: 10px; }
  `]
})
export class SalesChartDialogComponent implements OnInit {
  readonly metrics: { key: Metric; label: string }[] = [
    { key: 'books', label: 'Livros' }, { key: 'revenue', label: 'Faturamento' }, { key: 'orders', label: 'Pedidos' },
  ];
  readonly ranges = [{ days: 30, label: '30 dias' }, { days: 90, label: '90 dias' }, { days: 365, label: '12 meses' }];
  readonly granularities: { key: Granularity; label: string }[] = [
    { key: 'day', label: 'Dia' }, { key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' },
  ];

  metric: Metric = 'books';
  rangeDays = 30;
  granularity: Granularity = 'day';
  loading = false;
  error = '';
  points: AdminSalesTimeSeriesPoint[] = [];
  series: any[] = [];
  totalBooks = 0;
  totalOrders = 0;
  totalRevenue = 0;
  scheme: Color = { name: 'sales', selectable: false, group: ScaleType.Ordinal, domain: ['#5daf50'] };

  constructor(private dialogRef: MatDialogRef<SalesChartDialogComponent>, private sales: SalesService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void { this.load(); }

  get pointsDesc(): AdminSalesTimeSeriesPoint[] { return [...this.points].reverse(); }

  yFmt = (v: number) => this.metric === 'revenue'
    ? (v >= 1000 ? `R$ ${(v / 1000).toFixed(1)}k` : `R$ ${Math.round(v)}`)
    : String(Math.round(v));

  setMetric(m: Metric) { this.metric = m; this.scheme = { ...this.scheme, domain: [m === 'revenue' ? '#2f6fd6' : m === 'orders' ? '#7a5cc7' : '#5daf50'] }; this.buildSeries(); }
  setRange(days: number) { this.rangeDays = days; this.load(); }
  setGranularity(g: Granularity) { this.granularity = g; this.load(); }

  label(date: string): string {
    const d = new Date(date);
    return this.granularity === 'month' ? formatDate(d, 'MMM/yy', 'pt-BR') : formatDate(d, 'dd/MM', 'pt-BR');
  }

  private load() {
    this.loading = true;
    this.error = '';
    const end = new Date();
    const start = new Date(end.getTime() - this.rangeDays * 86400000);
    const iso = (d: Date) => formatDate(d, "yyyy-MM-dd'T'HH:mm:ss", 'en-US');
    this.sales.timeSeries({ dateStart: iso(start), dateEnd: iso(end), granularity: this.granularity }).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.isError) { this.error = res.errorDescription || 'Falha ao carregar a série.'; this.points = []; this.series = []; this.cdr.markForCheck(); return; }
        this.points = res.points ?? [];
        this.totalBooks = res.totalBooks; this.totalOrders = res.totalOrders; this.totalRevenue = res.totalRevenue;
        this.buildSeries();
      },
      error: (err) => { this.loading = false; this.error = err?.message || 'Erro inesperado.'; this.cdr.markForCheck(); },
    });
  }

  private buildSeries() {
    const name = this.metrics.find(m => m.key === this.metric)?.label ?? '';
    this.series = this.points.length ? [{ name, series: this.points.map(p => ({ name: this.label(p.date), value: p[this.metric] })) }] : [];
    this.cdr.markForCheck();
  }

  close() { this.dialogRef.close(); }
}
